/**
 * Client-side ObjectId generation.
 *
 * New nodes and edges are created optimistically in the browser and only then
 * sent to the server, so the id has to exist before the round trip. It must be a
 * real 24-hex ObjectId because publishing inserts Situation/Choice documents
 * using `_id = node.id`, and because carrying the id in the op is what makes a
 * retried create idempotent instead of a duplicate.
 *
 * Same layout as a MongoDB ObjectId (4-byte timestamp + 8 random bytes) without
 * pulling `bson` into the bundle. Uniqueness comes from 64 bits of randomness per
 * second, which is far beyond what a single author's session can collide in.
 */

const randomHex = (bytes) => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        const buffer = new Uint8Array(bytes);
        crypto.getRandomValues(buffer);
        return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    // Test environments and very old browsers. Math.random is weaker but the ids
    // are still scoped to one canvas and validated for uniqueness server-side.
    let out = '';
    for (let i = 0; i < bytes; i++) {
        out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    }
    return out;
};

/** @returns {string} A fresh 24-character hex id. */
export const newObjectId = () =>
    Math.floor(Date.now() / 1000).toString(16).padStart(8, '0') + randomHex(8);

export const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

/** @returns {boolean} Whether `value` could be used as a Mongo _id. */
export const isObjectId = (value) => typeof value === 'string' && OBJECT_ID_RE.test(value);

export default newObjectId;
